import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { loadDraft, setupAutosave, deleteDraft } from '../../utils/draftManager';
import { ATTRIBUTION_NOTICE } from '../../utils/attribution';

/**
 * NotesForm Component for creating and editing notes
 */

// Interfaces for the component props and form data
interface NoteFormProps {
  isEdit?: boolean;
  initialData?: NoteFormData;
  noteId?: string;
  onSubmit: (formData: NoteFormData) => Promise<string | void>;
  onChange?: (formData: NoteFormData) => void;
  isAuthenticated?: boolean;
  skipNavigation?: boolean; // Skip automatic navigation after submission
  onSubmitSuccess?: (noteId: string) => void; // Called after successful submission with noteId
}

export interface NoteFormData {
  note: string;
  contributorInfo?: {
    firstName: string;
    lastName: string;
    aliasName?: string;
    email: string;
  };
  location: {
    zipcode: string;
    city?: string;
    state?: string;
  };
  inspiration?: {
    sourceDisplayId?: string;
    inspirationNoteId?: string;
  };
  categories?: string[];
  tags?: string[];
}

// Get MAX_NOTE_LENGTH from environment variable with fallback to 1500
const MAX_NOTE_LENGTH = process.env.REACT_APP_MAX_NOTE_LENGTH 
  ? parseInt(process.env.REACT_APP_MAX_NOTE_LENGTH, 10) 
  : 1500; // Default to 1500 characters if not set

const NotesForm: React.FC<NoteFormProps> = ({
  isEdit = false,
  initialData,
  noteId,
  onSubmit,
  onChange,
  isAuthenticated,
  skipNavigation = false,
  onSubmitSuccess
}) => {
  const navigate = useNavigate();
  
  // Default form state
  const defaultFormData: NoteFormData = {
    note: '',
    contributorInfo: {
      firstName: '',
      lastName: '',
      aliasName: '',
      email: ''
    },
    location: {
      zipcode: ''
    },
    inspiration: {
      sourceDisplayId: '',
      inspirationNoteId: ''
    }
  };
  
  // Form state
  const [formData, setFormData] = useState<NoteFormData>(initialData || defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Reference to current form data for autosave
  const formDataRef = useRef(formData);
  
  // Update the ref when formData changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  // Setup autosave on component mount
  useEffect(() => {
    // Don't setup autosave for edit mode
    if (isEdit) return;
    
    // Check for existing draft on mount and load it if available
    const savedDraft = loadDraft();
    if (savedDraft && !initialData) {
      setFormData(savedDraft);
    }
    
    // Setup autosave interval
    const cleanup = setupAutosave(() => formDataRef.current);
    
    // Cleanup on unmount
    return cleanup;
  }, [isEdit, initialData]);
  
  // Update form if initial data changes (e.g., when loading existing note data)
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  
  // Handle text input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => {
        const updatedData = {
          ...prev,
          [parent]: {
            ...(prev[parent as keyof NoteFormData] as Record<string, unknown>),
            [child]: value
          }
        };
        
        // Call onChange if provided
        if (onChange) {
          onChange(updatedData);
        }
        
        return updatedData;
      });
    } else {
      setFormData(prev => {
        const updatedData = {
          ...prev,
          [name]: value
        };
        
        // Call onChange if provided
        if (onChange) {
          onChange(updatedData);
        }
        
        return updatedData;
      });
    }
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Note content validation
    if (!formData.note.trim()) {
      newErrors.note = 'Note content is required';
    } else if (formData.note.length > MAX_NOTE_LENGTH) {
      newErrors.note = `Note cannot exceed ${MAX_NOTE_LENGTH} characters`;
    }
    
    // Only validate contributor info if not in edit mode and user is not authenticated
    if (!isEdit && !isAuthenticated) {
      // Contributor info validation
      if (!formData.contributorInfo?.firstName.trim()) {
        newErrors['contributorInfo.firstName'] = 'First name is required';
      }
      
      if (!formData.contributorInfo?.lastName.trim()) {
        newErrors['contributorInfo.lastName'] = 'Last name is required';
      }
      
      if (!formData.contributorInfo?.email.trim()) {
        newErrors['contributorInfo.email'] = 'Email is required';
      } else if (!/^\S+@\S+\.\S+$/.test(formData.contributorInfo.email)) {
        newErrors['contributorInfo.email'] = 'Invalid email format';
      }
      
      // Location validation
      if (!formData.location.zipcode.trim()) {
        newErrors['location.zipcode'] = 'Zipcode is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.location.zipcode)) {
        newErrors['location.zipcode'] = 'Invalid zipcode format';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Clean up the form data before submitting
        const cleanedFormData = { ...formData };
        
        // Remove empty inspiration fields to prevent ObjectId cast errors
        if (cleanedFormData.inspiration) {
          if (!cleanedFormData.inspiration.sourceDisplayId || cleanedFormData.inspiration.sourceDisplayId === '') {
            delete cleanedFormData.inspiration.sourceDisplayId;
          }
          if (!cleanedFormData.inspiration.inspirationNoteId || cleanedFormData.inspiration.inspirationNoteId === '') {
            delete cleanedFormData.inspiration.inspirationNoteId;
          }
          
          // If inspiration object is empty after cleanup, remove it entirely
          if (Object.keys(cleanedFormData.inspiration).length === 0) {
            delete cleanedFormData.inspiration;
          }
        }
        
        const result = await onSubmit(cleanedFormData);

        // Delete draft on successful submission
        deleteDraft();

        // Track successful note creation in Google Analytics
        ReactGA.event({
          category: 'engagement',
          action: 'note_created',
          label: isAuthenticated ? 'authenticated' : 'anonymous',
        });

        // If onSubmitSuccess callback provided, call it with the noteId
        if (onSubmitSuccess && result) {
          onSubmitSuccess(result);
        }

        // Skip navigation if requested (e.g., for modal display)
        if (skipNavigation) {
          return;
        }

        // Redirect after successful submission
        if (isEdit) {
          navigate(`/my-notes/${noteId}`);
        } else {
          navigate('/my-notes');
        }
      } catch (error) {
        console.error('Error submitting note:', error);
        setSubmitError(
          error instanceof Error 
            ? error.message 
            : 'An unexpected error occurred. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  const characterCount = formData.note.length;
  const characterCountClass = characterCount > MAX_NOTE_LENGTH 
    ? 'text-danger' 
    : characterCount > MAX_NOTE_LENGTH * 0.8 
      ? 'text-warning' 
      : 'text-muted';
  
  return (
    <div className="notes-form-container">
      <h2>{isEdit ? 'Edit Note' : 'Submit a New Note'}</h2>
      
      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Note Content */}
        <div className="mb-3">
          <label htmlFor="note" className="form-label">Your Note <span className="text-danger">*</span></label>
          <textarea
            id="note"
            name="note"
            className={`form-control ${errors.note ? 'is-invalid' : ''}`}
            value={formData.note}
            onChange={handleChange}
            rows={6}
            placeholder="Write your note here..."
            maxLength={MAX_NOTE_LENGTH + 1} // Allow typing one more to trigger validation
            required
          />
          <div className={`character-count ${characterCountClass}`}>
            {characterCount}/{MAX_NOTE_LENGTH} characters
          </div>
          {errors.note && <div className="invalid-feedback">{errors.note}</div>}
          {!isEdit && isAuthenticated && (
            <div className="form-text" data-testid="attribution-notice">{ATTRIBUTION_NOTICE}</div>
          )}
        </div>
        
        {/* Only show contributor fields when creating a new note */}
        {!isEdit && !isAuthenticated && (
          <div className="contributor-info">
            <h4>Your Information</h4>

            {/* Privacy Notice */}
            <div className="alert mb-3" style={{
              backgroundColor: '#F0F7F4',
              border: '1px solid #7FA99B',
              color: '#1C2826'
            }}>
              <div className="d-flex align-items-start">
                <i className="fas fa-shield-alt me-2 mt-1" style={{ color: '#7FA99B' }}></i>
                <div>
                  <strong>Your Privacy</strong>
                  <ul className="mb-0 mt-1 ps-3" style={{ fontSize: '0.9rem' }}>
                    <li><strong>Publicly visible:</strong> Alias Name (or &ldquo;Anonymous&rdquo; if no alias) and City/State</li>
                    <li><strong>Never shared:</strong> Your full name and email address</li>
                  </ul>
                  <small className="text-muted">
                    <a href="/privacy" style={{ color: '#7FA99B', cursor: 'pointer' }}>Read our full Privacy Policy</a>
                  </small>
                </div>
              </div>
            </div>

            <div className="row">
              {/* First Name */}
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label">First Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  id="firstName"
                  name="contributorInfo.firstName"
                  className={`form-control ${errors['contributorInfo.firstName'] ? 'is-invalid' : ''}`}
                  value={formData.contributorInfo?.firstName || ''}
                  onChange={handleChange}
                  required
                />
                {errors['contributorInfo.firstName'] && (
                  <div className="invalid-feedback">{errors['contributorInfo.firstName']}</div>
                )}
              </div>
              
              {/* Last Name */}
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label">Last Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  id="lastName"
                  name="contributorInfo.lastName"
                  className={`form-control ${errors['contributorInfo.lastName'] ? 'is-invalid' : ''}`}
                  value={formData.contributorInfo?.lastName || ''}
                  onChange={handleChange}
                  required
                />
                {errors['contributorInfo.lastName'] && (
                  <div className="invalid-feedback">{errors['contributorInfo.lastName']}</div>
                )}
              </div>
            </div>
            
            <div className="row">
              {/* Alias Name */}
              <div className="col-md-6 mb-3">
                <label htmlFor="aliasName" className="form-label">Alias Name (Optional)</label>
                <input
                  type="text"
                  id="aliasName"
                  name="contributorInfo.aliasName"
                  className="form-control"
                  value={formData.contributorInfo?.aliasName || ''}
                  onChange={handleChange}
                  placeholder="Name to display publicly"
                />
                <div className="form-text" data-testid="attribution-notice">{ATTRIBUTION_NOTICE}</div>
              </div>
              
              {/* Email */}
              <div className="col-md-6 mb-3">
                <label htmlFor="email" className="form-label">Email <span className="text-danger">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="contributorInfo.email"
                  className={`form-control ${errors['contributorInfo.email'] ? 'is-invalid' : ''}`}
                  value={formData.contributorInfo?.email || ''}
                  onChange={handleChange}
                  required
                />
                {errors['contributorInfo.email'] && (
                  <div className="invalid-feedback">{errors['contributorInfo.email']}</div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Location Section */}
        <div className="location-info mb-3">
          <h4>Location</h4>
          <div className="row">
            {/* Zipcode */}
            <div className="col-md-6 mb-3">
              <label htmlFor="zipcode" className="form-label">
                Zipcode <span className="text-muted">{isAuthenticated ? '(Optional)' : '(Required)'}</span>
              </label>
              <input
                type="text"
                id="zipcode"
                name="location.zipcode"
                className={`form-control ${errors['location.zipcode'] ? 'is-invalid' : ''}`}
                value={formData.location.zipcode}
                onChange={handleChange}
                placeholder="e.g., 12345"
                required={!isAuthenticated}
              />
              {errors['location.zipcode'] && (
                <div className="invalid-feedback">{errors['location.zipcode']}</div>
              )}
              <div className="form-text">
                {isAuthenticated 
                  ? 'Leave empty to use the zipcode from your profile' 
                  : 'Your general location helps us show relevant regional content in clinics. No identifying information is stored.'}
              </div>
            </div>
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button
            type="button"
            className="btn btn-outline-primary me-md-2"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            style={{
              borderColor: '#7FA99B',
              color: '#7FA99B'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-submit-note"
            disabled={isSubmitting}
            style={{
              backgroundColor: '#E07A5F',
              borderColor: '#E07A5F',
              color: 'white'
            }}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEdit ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              isEdit ? 'Update Note' : 'Submit Note'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotesForm; 